import React from "react";
import { ChevronRight, Sparkles, CheckCircle2 } from "lucide-react";
import TopHeader from "@/components/layout/TopHeader";

/**
 * Progressive workspace stages (PD-009).
 * Every destination declares its current maturity stage so the placeholder
 * communicates *why* it looks the way it does and what comes next.
 */
export const MATURITY_STAGES = [
  { key: "concept", label: "Concept", note: "Purpose and vision defined" },
  { key: "foundation", label: "Foundation", note: "Navigation, layout and seeded data" },
  { key: "integrated", label: "Integrated", note: "Connected to CKB & live application data" },
  { key: "operational", label: "Operational", note: "Core workflows functional" },
  { key: "production", label: "Production Ready", note: "Polished and accepted" },
];

/**
 * Elegant destination placeholder — used by navigation targets whose
 * dedicated design has not yet been approved. Renders premium card
 * language consistent with approved dashboards so the app feels
 * connected rather than mocked.
 */
export default function DestinationPlaceholder({
  title,
  subtitle,
  Icon,
  accentColor = "#1e6de5",
  hero = {},
  overview = [],
  upcoming = [],
  maturity = "concept",
  currentDataSources = [],
}) {
  const heroBg = hero.gradient
    ? hero.gradient
    : `linear-gradient(115deg, #0e2a5c 0%, ${accentColor} 60%, #7ec2ff 100%)`;

  const stageIndex = MATURITY_STAGES.findIndex((s) => s.key === maturity);
  const currentStage = MATURITY_STAGES[stageIndex] || MATURITY_STAGES[0];

  return (
    <div data-testid={`cc-destination-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <TopHeader
        title={title}
        subtitle={subtitle}
        accent={<Icon className="w-4 h-4" style={{ color: accentColor }} />}
      />

      {/* Hero card */}
      <section
        className="cc-fade-up relative rounded-[28px] overflow-hidden mb-6 shadow-[0_25px_60px_-15px_rgba(11,48,96,0.28)]"
        style={{ background: heroBg }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] min-h-[280px]">
          <div className="p-8 lg:p-12 flex flex-col justify-center text-white relative z-10">
            <div
              data-testid={`cc-maturity-${maturity}`}
              className="inline-flex items-center gap-2 self-start rounded-full px-3 py-1.5 bg-white/15 border border-white/25 text-[11px] font-black tracking-[0.22em] backdrop-blur-md"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span>STAGE {stageIndex + 1} · {currentStage.label.toUpperCase()}</span>
            </div>
            <h2
              className="font-display font-black leading-[0.95] tracking-tight mt-5"
              style={{ fontSize: "clamp(38px, 4.5vw, 58px)" }}
            >
              {hero.title ?? title}
            </h2>
            {hero.tagline && (
              <p className="mt-3 text-[16px] font-medium text-white/90 max-w-[520px] leading-relaxed">
                {hero.tagline}
              </p>
            )}
            <div className="mt-6 flex flex-wrap gap-2">
              <button
                className="rounded-full px-5 py-2.5 text-[13px] font-semibold text-white/95 border border-white/30 hover:bg-white/10 transition-colors"
              >
                Notify me when ready
              </button>
              <button
                className="rounded-full px-5 py-2.5 text-[13px] font-semibold text-white/85 hover:bg-white/10 transition-colors"
              >
                View the roadmap
              </button>
            </div>

            {/* Decorative glow */}
            <div
              className="absolute -top-24 -left-24 w-[360px] h-[360px] rounded-full pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle at center, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 65%)",
              }}
            />
          </div>

          {/* Icon medallion */}
          <div className="relative flex items-center justify-center p-8">
            <div className="absolute inset-0" style={{
              background: "radial-gradient(circle at 65% 40%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 55%)"
            }} />
            <div
              className="relative w-40 h-40 rounded-full flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, rgba(255,255,255,0.18), rgba(255,255,255,0.04))",
                border: "1.5px solid rgba(255,255,255,0.3)",
                boxShadow: "0 24px 80px rgba(11,48,96,0.35)",
              }}
            >
              <Icon className="w-16 h-16 text-white" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </section>

      {/* Maturity strip */}
      <section className="cc-card cc-fade-up cc-delay-1 p-6 mb-6">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="cc-overline text-[#0952a1]">Progressive Workspace · PD-009</div>
            <h3 className="font-display font-bold text-[20px] text-[#0F172A] mt-1">
              This workspace is in the <span style={{ color: accentColor }}>{currentStage.label}</span> stage
            </h3>
            <p className="text-[13px] text-[#64748B] mt-1 max-w-2xl">
              {currentStage.note}. It graduates as CKB content and live data become available.
            </p>
          </div>
        </div>
        <ol className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {MATURITY_STAGES.map((s, i) => {
            const done = i < stageIndex;
            const active = i === stageIndex;
            return (
              <li
                key={s.key}
                className={`rounded-xl p-3 border ${
                  active
                    ? "border-transparent"
                    : done
                      ? "border-slate-100 bg-slate-50/60"
                      : "border-slate-100 opacity-60"
                }`}
                style={
                  active
                    ? {
                        background: `linear-gradient(135deg, ${accentColor}18, ${accentColor}08)`,
                        border: `1px solid ${accentColor}55`,
                      }
                    : {}
                }
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black"
                    style={{
                      background: done || active ? accentColor : "#E2E8F0",
                      color: done || active ? "white" : "#94A3B8",
                    }}
                  >
                    {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <div className="text-[12px] font-bold tracking-wide text-[#0F172A]">
                    {s.label}
                  </div>
                </div>
                <div className="text-[11px] text-[#64748B] mt-2 leading-snug">
                  {s.note}
                </div>
              </li>
            );
          })}
        </ol>
        {currentDataSources.length > 0 && (
          <div className="mt-5 pt-5 border-t border-slate-100">
            <div className="cc-overline text-[#94A3B8] mb-2">Currently powered by</div>
            <div className="flex flex-wrap gap-1.5">
              {currentDataSources.map((d) => (
                <span key={d} className="cc-chip cc-chip-ocean text-[11px]">{d}</span>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Overview + upcoming */}
      <section className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] gap-6">
        <div className="cc-card cc-fade-up cc-delay-2 p-7">
          <h3 className="font-display font-bold text-[22px] text-[#0F172A] mb-5">
            What lives here
          </h3>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {overview.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 p-4 rounded-xl border border-slate-100 hover:bg-slate-50/50 transition-colors"
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                  style={{ background: item.tint ?? "#EAF2FD" }}
                >
                  {item.Icon ? (
                    <item.Icon
                      className="w-4 h-4"
                      style={{ color: item.fg ?? accentColor }}
                    />
                  ) : (
                    <Sparkles className="w-4 h-4" style={{ color: item.fg ?? accentColor }} />
                  )}
                </div>
                <div>
                  <div className="text-[14px] font-semibold text-[#0F172A]">
                    {item.title}
                  </div>
                  <div className="text-[12px] text-[#64748B] mt-1 leading-relaxed">
                    {item.description}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="cc-card cc-fade-up cc-delay-3 p-7">
          <div className="cc-overline text-[#1e6de5] mb-3">Coming next</div>
          <h3 className="font-display font-bold text-[20px] text-[#0F172A] mb-4">
            On the roadmap
          </h3>
          <ul className="space-y-3">
            {upcoming.map((u, i) => (
              <li
                key={i}
                className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-b-0"
              >
                <span
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ background: accentColor }}
                />
                <span className="flex-1 text-[13px] font-medium text-[#0F172A]">
                  {u}
                </span>
                <ChevronRight className="w-4 h-4 text-[#94A3B8]" />
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
